import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { FINANCIAL_SOURCE, NUMBER_SEPARATOR } from '../constants/generalConstants';
import {
    CellIdType,
    CellMetadataType,
    ColIdType,
    ColumnMetadataType,
    FinancialSourceType,
    MetadataType,
    NumberFormatType,
    ReportPropsType,
    RowIdType,
    RowMetadataType,
    TableIdType,
} from '../types';
import {
    allPossibleImportCandidateColumnsSelected,
    allPossibleImportCandidateRowsSelected,
    getDefaultReportProps,
} from '../utils/metadataStoreUtils';
import {
    UNSUPPORTED_FOR_IMPORT_COLUMN_TYPES,
    UNSUPPORTED_FOR_IMPORT_ROW_TYPES,
} from '../components/annotations/constants';

/** Define the structure and operations for the metadata store */
type MetadataStoreType = {
    defaultReportProps: ReportPropsType;
    rowsMetadata: RowMetadataType[];
    colsMetadata: ColumnMetadataType[];
    numberFormat: NumberFormatType;
    cellsMetadata: CellMetadataType[];
    scale: number;
    financialSource: FinancialSourceType;

    actions: {
        getDefaultReportProps: () => ReportPropsType;

        // Methods to update individual row, column and cell metadata
        changeRowMetadata: (rowId: RowIdType, newRowMetadata: Partial<RowMetadataType>) => void;
        changeColMetadata: (collId: ColIdType, newCollMetadata: Partial<ColumnMetadataType>) => void;
        changeCellMetadata: (cellId: CellIdType, newCellMetadata: Partial<CellMetadataType>) => void;

        // Methods to completely replace all row, column and cell metadata
        setRowsMetadata: (rowsMetadata: RowMetadataType[]) => void;
        setColsMetadata: (colsMetadata: ColumnMetadataType[]) => void;
        setCellsMetadata: (cellsMetadata: CellMetadataType[]) => void;

        // Method to update individual number format props
        changeNumberFormat: (numberFormat: Partial<NumberFormatType>) => void;

        setScale: (scale: number) => void;

        setFinancialSource: (financialSource: FinancialSourceType) => void;

        //Method for complete replacement of store state
        setStoreState: (metadata: MetadataType) => void;

        toggleTableRowsImportCandidateStatus: (tableId: TableIdType) => void;
        toggleTableColsImportCandidateStatus: (tableId: TableIdType) => void;
    };
};

// Create a Zustand store for managing row and column metadata
const useMetadataStore = create<MetadataStoreType>()((set, get) => ({
    defaultReportProps: getDefaultReportProps(),
    // Initial state with empty metadata arrays
    rowsMetadata: [],
    colsMetadata: [],
    cellsMetadata: [],
    numberFormat: {
        decimalSeparator: NUMBER_SEPARATOR.UNKNOWN,
        negateDataInBrackets: false,
        thousandsSeparator: NUMBER_SEPARATOR.UNKNOWN,
        warnings: [],
    },
    scale: 1,
    financialSource: FINANCIAL_SOURCE.REPORT,

    actions: {
        getDefaultReportProps: () => ({ ...get().defaultReportProps }),

        /** Update a specific row's metadata while preserving other rows */
        changeRowMetadata: (rowId, newRowMetadata) =>
            set((state) => {
                const newRowsMetadata = state.rowsMetadata.map((prevRowMetadata) => {
                    if (prevRowMetadata.rowId === rowId) {
                        // Merge existing metadata with new metadata
                        return {
                            ...prevRowMetadata,
                            ...newRowMetadata,
                        };
                    } else {
                        return prevRowMetadata;
                    }
                });
                return { rowsMetadata: newRowsMetadata };
            }),

        /** Update a specific column's metadata while preserving other columns */
        changeColMetadata: (colId, newColMetadata) =>
            set((state) => {
                const newColsMetadata = state.colsMetadata.map((prevColMetadata) => {
                    if (prevColMetadata.colId === colId) {
                        // Merge existing metadata with new metadata
                        return {
                            ...prevColMetadata,
                            ...newColMetadata,
                        };
                    } else {
                        return prevColMetadata;
                    }
                });

                return { colsMetadata: newColsMetadata };
            }),

        /** Update a specific cell's metadata while preserving other cells */
        changeCellMetadata: (cellId, newCellMetadata) =>
            set((state) => {
                const newCellsMetadata = state.cellsMetadata.map((prevCellMetadata) => {
                    if (prevCellMetadata.cellId === cellId) {
                        return {
                            ...prevCellMetadata,
                            ...newCellMetadata,
                        };
                    } else {
                        return prevCellMetadata;
                    }
                });

                return { cellsMetadata: newCellsMetadata };
            }),

        /** Update a specific number format prop or entire object */
        changeNumberFormat: (newNumberFormat) =>
            set((state) => ({ numberFormat: { ...state.numberFormat, ...newNumberFormat } })),

        /** Completely replace all row metadata */
        setRowsMetadata: (rowsMetadata) => set(() => ({ rowsMetadata })),

        /** Completely replace all column metadata */
        setColsMetadata: (colsMetadata) => set(() => ({ colsMetadata })),

        /** Completely replace all cell metadata */
        setCellsMetadata: (cellsMetadata) => set(() => ({ cellsMetadata })),

        setScale: (scale) => set((state) => ({ ...state, scale })),

        setFinancialSource: (financialSource) => set((state) => ({ ...state, financialSource: financialSource })),

        /** Completely replaces store data */
        setStoreState: (metadata) =>
            set(() => ({
                rowsMetadata: metadata.rows,
                colsMetadata: metadata.columns,
                cellsMetadata: metadata.cells,
                numberFormat: metadata.numberFormat,
                scale: metadata.scaleFactor.value,
                financialSource: metadata.financialSource,
                defaultReportProps: getDefaultReportProps(metadata.columns),
            })),

        toggleTableRowsImportCandidateStatus: (tableId) => {
            const targetStatus = !allPossibleImportCandidateRowsSelected(get().rowsMetadata, tableId);

            const newRowsMetadata = get().rowsMetadata.map((row) => {
                if (row.rowId.includes(tableId)) {
                    if (UNSUPPORTED_FOR_IMPORT_ROW_TYPES.includes(row.contentType)) {
                        return row;
                    } else {
                        return {
                            ...row,
                            importCandidate: targetStatus,
                        };
                    }
                } else {
                    return row;
                }
            });

            get().actions.setRowsMetadata(newRowsMetadata);
        },

        toggleTableColsImportCandidateStatus: (tableId) => {
            const targetStatus = !allPossibleImportCandidateColumnsSelected(get().colsMetadata, tableId);

            const newColsMetadata = get().colsMetadata.map((col) => {
                if (col.colId.includes(tableId)) {
                    if (UNSUPPORTED_FOR_IMPORT_COLUMN_TYPES.includes(col.contentType)) {
                        return col;
                    } else {
                        return {
                            ...col,
                            importCandidate: targetStatus,
                        };
                    }
                } else {
                    return col;
                }
            });

            get().actions.setColsMetadata(newColsMetadata);
        },
    },
}));

export const useSetMetadataStoreState = () => useMetadataStore(useShallow((state) => state.actions.setStoreState));
export const useMetadataStoreState = () =>
    useMetadataStore(
        useShallow((state) => ({
            rowsMetadata: state.rowsMetadata,
            colsMetadata: state.colsMetadata,
            cellsMetadata: state.cellsMetadata,
            numberFormat: state.numberFormat,
            scale: state.scale,
            financialSource: state.financialSource,
        }))
    );

export const useGlobalMetadataState = () =>
    useMetadataStore(
        useShallow((state) => ({
            numberFormat: state.numberFormat,
            scale: state.scale,
            financialSource: state.financialSource,
        }))
    );

export const useMetadataSettingsSetters = () =>
    useMetadataStore(
        useShallow((state) => ({
            changeNumberFormat: state.actions.changeNumberFormat,
            setScale: state.actions.setScale,
            setFinancialSource: state.actions.setFinancialSource,
        }))
    );

export const useCellMetadata = (cellId: CellIdType) =>
    useMetadataStore(
        useShallow((state) => state.cellsMetadata.find((cellMetadata) => cellMetadata.cellId === cellId)!)
    );

export const useColumnMetadata = (colId: ColIdType) =>
    useMetadataStore(useShallow((state) => state.colsMetadata.find((col) => col.colId === colId)));

export const useIsColumnImportCandidate = (colId: ColIdType) =>
    useMetadataStore(useShallow((state) => state.colsMetadata.find((col) => col.colId === colId)?.importCandidate));

export const useChangeColumnMetadata = () => useMetadataStore((state) => state.actions.changeColMetadata);

export const useIsRowImportCandidate = (rowId: RowIdType) =>
    useMetadataStore(useShallow((state) => state.rowsMetadata.find((row) => row.rowId === rowId)?.importCandidate));

export const useChangeRowMetadata = () => useMetadataStore((state) => state.actions.changeRowMetadata);

export const useRowContentType = (rowId: RowIdType) =>
    useMetadataStore(useShallow((state) => state.rowsMetadata.find((row) => row.rowId === rowId)?.contentType));

export const useColumnContentType = (colId: ColIdType) =>
    useMetadataStore(
        useShallow((state) => state.colsMetadata.find((colMetadata) => colMetadata.colId === colId)?.contentType)
    );

export const useDefaultReportProps = () =>
    useMetadataStore(useShallow((state) => state.actions.getDefaultReportProps()));

export const useRowsImportCandidates = () =>
    useMetadataStore(
        useShallow((state) => state.rowsMetadata.filter((row) => row.importCandidate).map((row) => row.rowId))
    );

export const useMetadataStoreActions = () => useMetadataStore(useShallow((state) => state.actions));

export const useAllPossibleImportCandidateRowsSelected = (tableId: TableIdType) =>
    useMetadataStore(useShallow((state) => allPossibleImportCandidateRowsSelected(state.rowsMetadata, tableId)));

export const useAllPossibleImportCandidateColumnsSelected = (tableId: TableIdType) =>
    useMetadataStore(useShallow((state) => allPossibleImportCandidateColumnsSelected(state.colsMetadata, tableId)));

export const useColumnsMetadata = () => useMetadataStore(useShallow((state) => state.colsMetadata));
export default useMetadataStore;
